import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Session state: tokens plus the authenticated user.
 *
 * Lives in `shared/store/` rather than `features/auth/` because it is genuinely
 * cross-cutting — `shared/api/httpClient.ts` reads the access token on every
 * request and rotates it on a 401. A feature-owned store would force `shared`
 * to import a feature, inverting the dependency direction the whole codebase
 * relies on.
 *
 * Persisted to localStorage so a refresh doesn't log the user out. That is the
 * standard trade-off for a token-based SPA: it accepts XSS exposure of the
 * token in exchange for surviving a reload, which is why the access token is
 * short-lived and the refresh token rotates on every use with reuse detection
 * server-side.
 */

export interface SessionUser {
  id: number;
  email: string;
  fullName: string | null;
  role: 'owner' | 'manager' | 'staff';
  isActive: boolean;
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  user: SessionUser;
}

/**
 * Why the last session ended, when the login screen has something to say
 * about it. Kept here rather than in the navigation state: ending the session
 * makes the route guard redirect to the login screen with its own state, and
 * which of the two navigations lands last is React Router's call, not ours.
 */
export interface SignOutNotice {
  reason: 'passwordChanged';
  email: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  /** Never persisted: a reload of the login screen starts clean. */
  signOutNotice: SignOutNotice | null;
}

interface AuthActions {
  setSession: (session: Session) => void;
  /** Replaces the signed-in user (after editing her profile), keeping the tokens. */
  setUser: (user: SessionUser) => void;
  clearSession: (notice?: SignOutNotice) => void;
}

type AuthStore = AuthState & AuthActions;

const STORAGE_KEY = 'grazia.session';

const EMPTY: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  signOutNotice: null,
};

const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...EMPTY,
      setSession: ({ accessToken, refreshToken, user }) =>
        set({ accessToken, refreshToken, user, signOutNotice: null }),
      setUser: (user) => set({ user }),
      clearSession: (notice) => set({ ...EMPTY, signOutNotice: notice ?? null }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    }
  )
);

/** Reads the current access token outside React (used by `httpClient`). */
export const getAccessToken = (): string | null => useAuthStore.getState().accessToken;

/** True when a session exists. Not a validity check — the API is the authority. */
export const hasSession = (): boolean => Boolean(useAuthStore.getState().accessToken);

export default useAuthStore;
